import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import Input from "../../widgets/Input";
import Tooltip from "../../widgets/ToolTip";

interface Role {
  _id?: string;
  role_key: string;
  role_name: string;
  role_description: string;
}

interface RoleFormProps {
  isOpen: boolean;
  onClose: () => void;
  role?: Role | null;
  onSubmit: (data: Role) => void;
  isLoading?: boolean;
}

interface RoleFormData {
  role_key: string;
  role_name: string;
  role_description: string;
}

const RoleForm = ({
  isOpen,
  onClose,
  role,
  onSubmit,
  isLoading = false,
}: RoleFormProps) => {
  const { control, handleSubmit, reset } = useForm<RoleFormData>({
    mode: "onChange",
    defaultValues: {
      role_key: role?.role_key || "",
      role_name: role?.role_name || "",
      role_description: role?.role_description || "",
    },
  });

  React.useEffect(() => {
    if (role) {
      reset({
        role_key: role.role_key,
        role_name: role.role_name,
        role_description: role.role_description,
      });
    } else {
      reset({
        role_key: "",
        role_name: "",
        role_description: "",
      });
    }
  }, [role, isOpen, reset]);

  const handleFormSubmit = (data: RoleFormData) => {
    onSubmit({
      ...data,
      _id: role?._id,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {role ? "Edit Role" : "Add Role"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            disabled={isLoading}
          >
            <Tooltip content="Close">
            <X className="w-6 h-6" />
            </Tooltip>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          <div className="space-y-4">
            <Input
              name="role_key"
              control={control}
              label="Role Key"
              placeholder="Enter role key (e.g., admin, user)"
              rules={{
                required: "Role key is required",
                pattern: {
                  value: /^[a-z_]+$/,
                  message:
                    "Role key must contain only lowercase letters and underscores",
                },
              }}
              disabled={role as any}
            />

            <Input
              name="role_name"
              control={control}
              label="Role Name"
              placeholder="Enter role name (e.g., Administrator)"
              rules={{
                required: "Role name is required",
                minLength: {
                  value: 2,
                  message: "Role name must be at least 2 characters",
                },
                pattern: {
                  value: /^[A-Za-z0-9\s]*$/,
                  message: "Only alphabets, numbers, and spaces are allowed"
                }
              }}
              disabled={isLoading}
            />

            <Input
              name="role_description"
              control={control}
              label="Role Description"
              placeholder="Enter role description (optional)"
              rules={{
                pattern: {
                  value: /^[A-Za-z0-9\s_.,-]*$/,
                  message: "Only alphabets, numbers, spaces, hyphens, underscores, commas and periods are allowed"
                }
              }}
              disabled={isLoading}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : role ? "Update Role" : "Create Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleForm;
